// UI-string localization (separate from content localization -- see docs/LOCALIZATION.md).
// Only a subset of the app's copy is wired through this yet (nav, login, settings tab labels)
// -- most pages still have hardcoded English JSX. Converting the rest is tracked as a known gap,
// not silently assumed done.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import es from './locales/es.json';
import hi from './locales/hi.json';
import zh from './locales/zh.json';
import journeyEn from './locales/journey.en.json';
import journeyEs from './locales/journey.es.json';
import journeyHi from './locales/journey.hi.json';
import journeyZh from './locales/journey.zh.json';
import type { Locale } from '../api/types';

const STORAGE_KEY = 'omnist.locale';
const SUPPORTED: Locale[] = ['en', 'es', 'hi', 'zh'];

function detectInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored as Locale)) return stored as Locale;
  } catch {
    // localStorage can throw in some private-browsing contexts -- fall through to detection.
  }
  const browserLang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED.includes(browserLang as Locale) ? (browserLang as Locale) : 'en';
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: { ...en, ...journeyEn } },
    es: { translation: { ...es, ...journeyEs } },
    hi: { translation: { ...hi, ...journeyHi } },
    zh: { translation: { ...zh, ...journeyZh } },
  },
  lng: detectInitialLocale(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

/** Keeps <html lang> in step with the UI language (review fixes F7/F12): screen readers choose a
 * pronunciation voice from it, and browsers use it to hyphenate long labels. */
function syncDocumentLanguage(lng: string | undefined): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng || 'en';
}
syncDocumentLanguage(i18n.resolvedLanguage ?? i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

/** Switches the app's UI language and remembers it for next visit (pre-login pages have no
 * signed-in profile to read a preference from). Once signed in, UserContext additionally syncs
 * this to the caller's stored `locale` field via PUT /api/me -- see its `setLocale`. */
export function setAppLanguage(locale: Locale): void {
  void i18n.changeLanguage(locale);
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // best-effort persistence only
  }
}

/** Each language's own name, in its own script -- a language picker labels options natively. */
export const LOCALE_NATIVE_NAMES: Record<Locale, string> = { en: 'English', es: 'Español', hi: 'हिन्दी', zh: '中文' };

/** The active UI language, narrowed to a supported Locale. */
export function currentLocale(): Locale {
  const lng = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2) as Locale;
  return SUPPORTED.includes(lng) ? lng : 'en';
}

export { SUPPORTED as SUPPORTED_LOCALES };
export default i18n;
